/**
 * Izi API client.
 *
 * The website only talks to one public, unauthenticated endpoint:
 *
 *   POST {API}/api/v1/contact-messages
 *   body: { name, email, topic?, message }
 *   -> 201 { contactMessage: {...} }
 *
 * That endpoint is the platform's public inbox (routes/contactMessage.routes.js
 * — the POST is explicitly PUBLIC; the inbox and triage are admin-only). Table
 * reservations under /api/v1/tables/:restaurantId/reservations require an
 * authenticated staff token, so a public website cannot call them directly.
 * Bookings are therefore submitted as contact messages with a structured body
 * and a `topic` of "Reservation — <branch>", which lands them in the same
 * dashboard inbox for the team to confirm.
 *
 * Required backend config: add this site's origin to CLIENT_URLS so CORS
 * (app.js -> config.corsOrigins) accepts the request.
 */

const API_ROOT = (process.env.REACT_APP_IZI_API_URL || 'https://www.iziwork.online').replace(/\/+$/, '');
export const API_BASE = `${API_ROOT}/api/v1`;

const TIMEOUT_MS = 15000;

export class ApiError extends Error {
  constructor(message, { status, fieldErrors } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors || null;
  }
}

async function post(path, body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Lets the platform attribute submissions to the marketing site.
        'X-Client-Platform': 'web-thelocal',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timer);
    throw new ApiError(
      err.name === 'AbortError'
        ? 'That took too long. Check your connection and try again.'
        : 'We could not reach our servers. Please try again, or call the restaurant directly.'
    );
  }
  clearTimeout(timer);

  let payload = null;
  try {
    payload = await response.json();
  } catch (_) {
    payload = null;
  }

  if (!response.ok) {
    const fieldErrors = payload?.errors || payload?.details || null;
    throw new ApiError(
      payload?.message || 'Something went wrong on our side. Please try again.',
      { status: response.status, fieldErrors }
    );
  }

  return payload;
}

/** General "Contact us" submission. */
export function sendContactMessage({ name, email, phone, topic, message }) {
  const lines = [message.trim()];
  if (phone) lines.push('', `Telephone: ${phone}`);
  return post('/contact-messages', {
    name: name.trim(),
    email: email.trim(),
    topic: (topic || 'General enquiry').slice(0, 60),
    message: lines.join('\n').slice(0, 2000),
  });
}

/**
 * Table booking. Flattened into the contact-message shape so the whole request
 * survives in one readable block in the dashboard inbox.
 */
export function sendBookingRequest({
  name,
  reservationName,
  email,
  phone,
  branch,
  date,
  time,
  guests,
  details,
}) {
  const message = [
    'NEW TABLE REQUEST',
    '',
    `Branch:            ${branch}`,
    `Date:              ${date}`,
    `Time:              ${time}`,
    `Guests:            ${guests}`,
    `Reservation name:  ${reservationName || name}`,
    `Booked by:         ${name}`,
    `Telephone:         ${phone}`,
    `Email:             ${email}`,
    '',
    'Additional details:',
    details?.trim() ? details.trim() : '—',
  ].join('\n');

  return post('/contact-messages', {
    name: name.trim(),
    email: email.trim(),
    topic: `Reservation — ${branch}`.slice(0, 60),
    message: message.slice(0, 2000),
  });
}

/* --------------------------------------------------------------------------
   Netlify Forms copy — the email channel.

   Every booking is ALSO posted to Netlify Forms (form name "booking"), which
   Netlify records in its dashboard and forwards by email once a notification
   is configured (Site settings > Forms > Form notifications). This runs in
   parallel with the Izi API call so a reservation lands in both inboxes; on
   localhost (no Netlify runtime) the POST simply 404s and is ignored.

   The matching hidden <form name="booking" data-netlify="true"> lives in
   public/index.html — Netlify discovers forms at deploy time by scanning the
   built HTML, so keep its field list in sync with the payload below.
   -------------------------------------------------------------------------- */

export async function sendBookingEmailCopy(values) {
  const payload = {
    'form-name': 'booking',
    branch: values.branch || '',
    date: values.date || '',
    time: values.time || '',
    guests: String(values.guests || ''),
    name: values.name || '',
    reservationName: values.reservationName || '',
    email: values.email || '',
    phone: values.phone || '',
    details: values.details || '',
  };
  const res = await fetch('/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(payload).toString(),
  });
  if (!res.ok) throw new ApiError('Email copy failed', { status: res.status });
  return true;
}
